import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import Button from '../../action/Button';
import { useLanguage } from '@/chassis/ui/i18n/useLanguage';
import type { Language } from '@/chassis/ui/i18n/i18n-helper';

const paginationLabels: Record<'prev' | 'next', Record<Language, string>> = {
    prev: { pt: 'Anterior', en: 'Previous', es: 'Anterior' },
    next: { pt: 'Próximo', en: 'Next', es: 'Siguiente' },
};

type NativeNavProps = Omit<ComponentPropsWithoutRef<'nav'>, 'children'>;

interface PaginationProps extends NativeNavProps {
    children?: ReactNode;
    currentPage?: number;
    totalPages?: number;
    onPageChange?: (page: number) => void;
}

type PaginationLinkProps = ComponentPropsWithoutRef<typeof Button> & {
    active?: boolean;
};

function Pagination({ className, children, currentPage, totalPages, onPageChange, ...navProps }: PaginationProps) {
    const lang = useLanguage();
    const isControlled = typeof currentPage === 'number' && typeof totalPages === 'number' && totalPages > 0 && typeof onPageChange === 'function';

    const clampedCurrent = isControlled ? Math.min(Math.max(currentPage ?? 1, 1), totalPages as number) : undefined;

    const gotoPage = (page: number) => {
        if (!isControlled || page === clampedCurrent) {
            return;
        }
        onPageChange?.(page);
    };

    const buildPages = () => {
        if (!isControlled || !clampedCurrent) {
            return children;
        }

        const pages: Array<number | 'ellipsis'> = [];
        const total = totalPages as number;

        if (total <= 7) {
            for (let page = 1; page <= total; page += 1) {
                pages.push(page);
            }
        } else {
            const start = Math.max(2, clampedCurrent - 1);
            const end = Math.min(total - 1, clampedCurrent + 1);

            pages.push(1);
            if (start > 2) {
                pages.push('ellipsis');
            }
            for (let page = start; page <= end; page += 1) {
                pages.push(page);
            }
            if (end < total - 1) {
                pages.push('ellipsis');
            }
            pages.push(total);
        }

        return [
            <Pagination.Link key="prev" as="button" type="button" onClick={() => gotoPage((clampedCurrent as number) - 1)} disabled={clampedCurrent === 1}>
                {paginationLabels.prev[lang]}
            </Pagination.Link>,
            ...pages.map((page, index) => {
                if (page === 'ellipsis') {
                    return (
                        <li key={`ellipsis-${index}`} className="flex-1 sm:flex-initial">
                            <span className="flex h-9 items-center px-2 text-sm text-neutro-600">&hellip;</span>
                        </li>
                    );
                }

                return (
                    <Pagination.Link
                        key={page}
                        as="button"
                        type="button"
                        onClick={() => gotoPage(page)}
                        active={page === clampedCurrent}
                    >
                        {page}
                    </Pagination.Link>
                );
            }),
            <Pagination.Link
                key="next"
                as="button"
                type="button"
                onClick={() => gotoPage((clampedCurrent as number) + 1)}
                disabled={clampedCurrent === (totalPages as number)}
            >
                {paginationLabels.next[lang]}
            </Pagination.Link>,
        ];
    };

    return (
        <nav className={className} {...navProps}>
            <ul className="flex w-full mr-0 sm:w-auto sm:mr-auto">{buildPages()}</ul>
        </nav>
    );
}

Pagination.Link = ({ className, active, children, as: asProp, ...buttonProps }: PaginationLinkProps) => {
    return (
        <li className="flex-1 sm:flex-initial">
            <Button
                {...buttonProps}
                as={asProp ?? 'a'}
                className={twMerge([
                    // Rodada 2 (28/10): página atual em Roxo Canal; demais neutras com hover Neutro 100.
                    'min-w-0 sm:min-w-[36px] h-9 font-mono text-[13px] font-medium flex items-center justify-center text-neutro-800 sm:mr-1.5 dark:text-slate-300 px-1 sm:px-3 py-0 shadow-none',
                    active && 'bg-primary border-primary text-white dark:bg-primary dark:border-primary dark:text-white',
                    !active && 'border-transparent bg-transparent [&:hover:not(:disabled)]:bg-neutro-100 [&:hover:not(:disabled)]:border-neutro-300 dark:[&:hover:not(:disabled)]:bg-darkmode-500',
                    buttonProps.disabled && 'opacity-60 cursor-not-allowed',
                    className,
                ])}
            >
                {children}
            </Button>
        </li>
    );
};

export default Pagination;
